/*
 * KeywordPriorityScorer
 * Ranks up to 5 of the reader's own keywords using the article's intent × case-value matrix.
 *
 * Rules:
 * - A row counts once it has a keyword, monthly searches (whole number >= 0), an intent, and an
 *   average fee for that case type (number > 0). Incomplete rows are ignored; invalid numbers
 *   show a message for that row.
 * - Intent "Comparing firms" and "Ready to hire" count as hire-side; "Researching" is research-side.
 * - Case value is "higher" when the row's fee is at or above the MEDIAN fee of the reader's own
 *   valid rows (so the comparison is only ever against their own numbers). With one row, it is
 *   treated as higher.
 * - Category: hire-side + higher -> "Build first"; hire-side + lower -> "Build next";
 *   research-side + higher -> "Supporting content"; research-side + lower -> "Later".
 * - Within a category, rows are ordered by searches × fee, shown only as a relative ranking,
 *   never as a revenue forecast.
 * No benchmarks, defaults, tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Intent = "" | "research" | "compare" | "hire";
type Row = { keyword: string; searches: string; intent: Intent; fee: string };

const EMPTY_ROW: Row = { keyword: "", searches: "", intent: "", fee: "" };

const CATEGORIES = [
  { key: "first", label: "Build first", note: "A strong practice or sub-practice page with clear contact options." },
  { key: "next", label: "Build next", note: "Worth its own page once the first group is live and tracked." },
  { key: "support", label: "Supporting content", note: "Answer the question well, then link to the practice page it supports." },
  { key: "later", label: "Later", note: "Cover only when it helps a real client question." },
] as const;

type CatKey = (typeof CATEGORIES)[number]["key"];

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

export default function KeywordPriorityScorer() {
  const baseId = useId();
  const [rows, setRows] = useState<Row[]>([{ ...EMPTY_ROW }, { ...EMPTY_ROW }, { ...EMPTY_ROW }]);

  const update = (i: number, field: keyof Row, value: string) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const errors: Record<number, string> = {};
  const valid: { i: number; keyword: string; searches: number; intent: Intent; fee: number }[] = [];
  rows.forEach((r, i) => {
    const complete = r.keyword.trim() && r.searches.trim() !== "" && r.intent && r.fee.trim() !== "";
    if (!complete) return;
    const searches = Number(r.searches);
    const fee = Number(r.fee);
    if (!Number.isInteger(searches) || searches < 0) errors[i] = "Monthly searches must be a whole number of 0 or more.";
    else if (!Number.isFinite(fee) || fee <= 0) errors[i] = "Average fee must be more than 0.";
    else valid.push({ i, keyword: r.keyword.trim(), searches, intent: r.intent, fee });
  });

  const med = valid.length > 0 ? median(valid.map((v) => v.fee)) : 0;
  const ranked = valid.map((v) => {
    const hireSide = v.intent === "compare" || v.intent === "hire";
    const higher = valid.length === 1 || v.fee >= med;
    const cat: CatKey = hireSide ? (higher ? "first" : "next") : higher ? "support" : "later";
    return { ...v, cat, score: v.searches * v.fee };
  });

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const inputCls = `mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`;

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Which keywords should get a page first?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Add up to five keywords with your own figures. Case value is compared only against the other keywords you enter.
        Nothing is saved or sent.
      </p>

      <div className="mt-5 space-y-4">
        {rows.map((r, i) => {
          const id = `${baseId}-${i}`;
          return (
            <fieldset key={i} className="rounded-lg border border-border p-4">
              <legend className="px-1 text-sm font-semibold">Keyword {i + 1}</legend>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-2 lg:col-span-1">
                  <label htmlFor={`${id}-kw`} className="block text-sm font-medium">Keyword</label>
                  <Input id={`${id}-kw`} type="text" value={r.keyword} onChange={(e) => update(i, "keyword", e.target.value)} placeholder="e.g. truck accident lawyer [city]" className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`${id}-vol`} className="block text-sm font-medium">Monthly searches</label>
                  <Input id={`${id}-vol`} type="number" inputMode="numeric" min={0} step={1} value={r.searches} onChange={(e) => update(i, "searches", e.target.value)} placeholder="e.g. 90" className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`${id}-int`} className="block text-sm font-medium">Searcher is…</label>
                  <select id={`${id}-int`} value={r.intent} onChange={(e) => update(i, "intent", e.target.value)} className={inputCls}>
                    <option value="">Choose…</option>
                    <option value="research">Researching</option>
                    <option value="compare">Comparing firms</option>
                    <option value="hire">Ready to hire</option>
                  </select>
                </div>
                <div>
                  <label htmlFor={`${id}-fee`} className="block text-sm font-medium">Your average fee for this case type ($)</label>
                  <Input id={`${id}-fee`} type="number" inputMode="decimal" min={0} step="any" value={r.fee} onChange={(e) => update(i, "fee", e.target.value)} placeholder="e.g. 8000" className={inputCls} />
                </div>
              </div>
              {errors[i] && <p className="mt-2 text-sm">{errors[i]}</p>}
            </fieldset>
          );
        })}
      </div>

      {rows.length < 5 && (
        <Button variant="outline"
          type="button"
          onClick={() => setRows((p) => [...p, { ...EMPTY_ROW }])}
          className={`mt-3 rounded-md border border-input bg-card px-4 py-2 text-sm font-medium ${focusRing}`}
        >
          Add another keyword
        </Button>
      )}

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {ranked.length === 0 ? (
          <p className="text-sm text-muted-foreground">Fill in at least one complete row to see where it ranks.</p>
        ) : (
          <div className="space-y-3">
            {CATEGORIES.map((c) => {
              const items = ranked.filter((r) => r.cat === c.key).sort((a, b) => b.score - a.score);
              if (items.length === 0) return null;
              return (
                <div key={c.key} className="rounded-md border border-border bg-card p-3 text-sm">
                  <p className="font-semibold">{c.label}</p>
                  <p className="text-muted-foreground">{c.note}</p>
                  <ol className="mt-2 list-decimal pl-5">
                    {items.map((it) => (
                      <li key={it.i}>{it.keyword}</li>
                    ))}
                  </ol>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground">
              Within each group, keywords are ordered by searches × fee. That's a way to rank them against each other,
              not a prediction of revenue.
            </p>
            <p className="text-sm">
              Want this done across your whole keyword list?{" "}
              <Link to="/services/$slug" params={{ slug: "law-firm-seo" }} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Law firm SEO →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
